import { MathUtils } from "three";
import { UUID } from "../../utils/typeDefinitions";
import ISceneObject from "../sceneObject/ISceneObject";
import createSceneObject from '../../model/sceneObject/SceneObjectFactory';
import createSceneObjectData from '../sceneObjectData/SceneObjectDataFactory';
import apiDocumentSchema from "../../services/bimVisualizerApi/schema/document/apiDocumentSchema";
import UserRight from "../UserRight";
import DocumentEventType from "../../enums/DocumentEventType";

type DocumentEventListener = (document: Document, sceneObject?: ISceneObject) => void;

export default class Document {
    static readonly CURRENT_VERSION: number = 1;
    documentUuid: UUID;
    sceneObjects: ISceneObject[] = [];
    name: string;
    version: number = Document.CURRENT_VERSION;
    userRight: UserRight = UserRight.NoRights();

    private listeners: Map<DocumentEventType, DocumentEventListener[]> = new Map();

    constructor(name: string, documentUuid?: UUID) {
        this.documentUuid = documentUuid ?? MathUtils.generateUUID();
        this.name = name;
        this.listeners.set(DocumentEventType.Add, []);
        this.listeners.set(DocumentEventType.Remove, []);
        this.listeners.set(DocumentEventType.RemoveAll, []);
        this.listeners.set(DocumentEventType.ContentsChanged, []);
    }

    // Add a listener for a specific event type
    addEventListener(eventType: DocumentEventType, listener: DocumentEventListener): void {
        const listeners = this.listeners.get(eventType);
        if (listeners) {
            listeners.push(listener);
        }
    }

    // Remove a specific listener for an event type
    removeEventListener(eventType: DocumentEventType, listener: DocumentEventListener): void {
        const listeners = this.listeners.get(eventType);
        if (listeners) {
            const index = listeners.indexOf(listener);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        }
    }

    addToScene() {
        this.sceneObjects.forEach(sceneObject => {
            sceneObject.addToScene();
        });
        this.outputState();
    }

    // Shows/hides already-added scene objects based on the given scope, without touching whether
    // they're attached to the THREE scene graph. Unlike filtering in addToScene, this can be called
    // at any time - including after documents are already staged - so it's safe to (re)apply whenever
    // the scope changes, regardless of whether the target uuid was known yet at staging time.
    applyScope(allowedUuids: ReadonlySet<UUID> | null | undefined) {
        this.sceneObjects.forEach(sceneObject => {
            if (!allowedUuids || allowedUuids.has(sceneObject.data.uuid)) {
                sceneObject.geometry.show();
            } else {
                sceneObject.geometry.hide();
            }
        });
    }

    addToDocument(sceneObject: ISceneObject) {
        this.sceneObjects.push(sceneObject);
        this.notifyListeners(DocumentEventType.Add, sceneObject);
        this.outputState();
    }
    
    removeFromDocument(sceneObject: ISceneObject) {
        const index = this.sceneObjects.indexOf(sceneObject);
        if (index > -1) {
            this.sceneObjects.splice(index, 1);
            this.notifyListeners(DocumentEventType.Remove, sceneObject);
        }
        this.outputState();
    }

    removeAllFromDocument() {
        this.sceneObjects = [];
        this.notifyListeners(DocumentEventType.RemoveAll);
        this.outputState();
    }

    private notifyListeners(eventType: DocumentEventType, sceneObject?: ISceneObject) {
        const listeners = this.listeners.get(eventType);
        if (listeners) {
            listeners.forEach(listener => listener(this, sceneObject));
        }

        if (eventType !== DocumentEventType.ContentsChanged) {
            this.notifyListeners(DocumentEventType.ContentsChanged, undefined);
        }
    }

    toJson(): any {
        return {
            documentUuid: this.documentUuid,
            name: this.name,
            sceneObjects: this.sceneObjects.map(obj => obj.data.toJson())
        };
    }

    static fromJson(json: any): Document {

        const jsonDoc = apiDocumentSchema.parse(json);

        const document = new Document(jsonDoc.name);
        document.documentUuid = jsonDoc.documentUuid;
        document.version = jsonDoc.version;
        document.userRight = UserRight.fromSchema(jsonDoc.userRight);
        document.sceneObjects = jsonDoc.sceneObjects 
            ?   jsonDoc.sceneObjects.map((data: any) => {
                    const sceneObjectData = createSceneObjectData(data);
                    const sceneObject =  createSceneObject(sceneObjectData);
                    return sceneObject;
                })
            : [];
        return document;
    }

    getSceneObject(uuid: UUID): ISceneObject | undefined {
        return this.sceneObjects.find(obj => obj.data.uuid === uuid);
    }

    outputState() {
        console.log(`Document ${this.name} contains ${this.sceneObjects.length} scene objects. (Document-id: ${this.documentUuid})`);
    }

    onAnnotationDirectionChange(rotationOffset: number) {
        this.sceneObjects.forEach(obj => {
                obj.onAnnotationDirectionChange(rotationOffset);
        });
    }
}